<template>
  <Content>
    <template #body>
      <Form
        :scheme="scheme"
        :method-fetch-data="methods.fetchData"
        :method-create-data="methods.createData"
        :method-update-data-idempotent="methods.updateData"
        :method-update-scheme="methods.updateScheme"
        redirect-to-edit="/publication/edit"
      />
    </template>
  </Content>
</template>

<script>
import Form from "@/components/Form";
import Content from "@/layout/Content";

import {
  createPublication,
  getPublication,
  updatePublication,
  addPublicationGalleryItem,
  deletePublicationGalleryItem,
  addPublicationRelated,
  deletePublicationRelated,
} from "@/api/publication";
import { PublicationCreate as defaultScheme } from "@/api/schemes/publication";
import { getSiteSections } from "@/api/siteSection";
import { getDigests } from "@/api/digest";

const updateScheme = async (scheme) => {
  const { properties = {} } = scheme;

  const [siteSectionsResponse, digestsResponse] = await Promise.all([
    getSiteSections(),
    getDigests(),
  ]);

  const siteSections = siteSectionsResponse.items || siteSectionsResponse || [];
  const digests = digestsResponse.items || digestsResponse || [];

  if (properties.site_section) {
    const formatted = siteSections.map((item) => ({
      [properties.site_section.params?.label || "name"]:
        item.name || item.title || `Элемент ${item.id}`,
      [properties.site_section.params?.key || "id"]: item.id,
    }));
    Object.assign(properties.site_section, {
      items: formatted,
      params: properties.site_section.params || {
        key: "id",
        label: "name",
        multiple: false,
      },
    });
  }

  if (properties.digest) {
    const formatted = digests.map((item) => ({
      [properties.digest.params?.label || "name"]:
        item.name || item.title || `Элемент ${item.id}`,
      [properties.digest.params?.key || "id"]: item.id,
    }));
    Object.assign(properties.digest, {
      items: formatted,
      params: properties.digest.params || {
        key: "id",
        label: "name",
        multiple: false,
      },
    });
  }

  return scheme;
};

function normalizeGalleryFromUpload(form) {
  const uploadItems = form.gallery_upload || form.gallery || [];

  return (uploadItems || []).map((item, index) => {
    const url = item.url || item.media_url || item;
    const type = item.type || item.media_type || "image";
    const sortOrder = index + 1;

    return {
      media_url: url || "",
      media_title: item.media_title ?? null,
      media_type: type || "image",
      sort_order: sortOrder,
    };
  });
}

async function preparePublicationForm(data) {
  const gallery = data.gallery || [];
  const relations = data.publications_related || [];

  data.gallery_upload = gallery.map((item) => ({
    id: item.id,
    url: item.media_url,
    type: item.media_type || "image",
    sort_order: item.sort_order,
  }));

  if (relations.length) {
    const uniqueIds = Array.from(
      new Set(relations.map((item) => item.related_publication).filter(Boolean))
    );

    const relatedPublicationsResponses = await Promise.all(
      uniqueIds.map((relatedId) => getPublication(relatedId).catch(() => null))
    );

    const relatedById = new Map();
    uniqueIds.forEach((id, index) => {
      const resp = relatedPublicationsResponses[index];
      if (resp && resp.title) {
        relatedById.set(id, resp.title);
      }
    });

    data.publications_related = relations.map((item) => ({
      related_publication: item.related_publication,
      related_publication_title:
        relatedById.get(item.related_publication) || "",
    }));
  } else {
    data.publications_related = [];
  }

  return data;
}

function buildCreatePayload(form) {
  const site_section = Array.isArray(form.site_section)
    ? form.site_section[0]
    : form.site_section;
  const digest = Array.isArray(form.digest) ? form.digest[0] : form.digest;

  const publications_related = normalizeRelatedPublications(form);

  return {
    title: form.title,
    description: form.description,
    content: form.content,
    banner_image: form.banner_image || "",
    site_section,
    digest,
    published: !!form.published,
    sort_order: form.sort_order ?? null,
    gallery: normalizeGalleryFromUpload(form),
    publications_related,
  };
}

function buildUpdatePayload(form) {
  const site_section = Array.isArray(form.site_section)
    ? form.site_section[0]
    : form.site_section;
  const digest = Array.isArray(form.digest) ? form.digest[0] : form.digest;

  return {
    title: form.title,
    description: form.description,
    content: form.content,
    banner_image: form.banner_image ?? null,
    site_section,
    digest,
    published: form.published,
    sort_order: form.sort_order ?? null,
  };
}

function normalizeRelatedPublications(form, limit = 3) {
  const raw = (form.publications_related || [])
    .map((r) =>
      r && r.related_publication !== undefined && r.related_publication !== null
        ? Number(r.related_publication)
        : null
    )
    .filter((v) => typeof v === "number" && !Number.isNaN(v));

  const unique = [];
  const seen = new Set();
  for (const id of raw) {
    if (seen.has(id)) continue;
    seen.add(id);
    unique.push(id);
    if (unique.length >= limit) break;
  }

  return unique.map((id) => ({ related_publication: id }));
}

async function createData(form) {
  return createPublication(buildCreatePayload(form));
}

async function updateData(id, form) {
  await updatePublication(id, buildUpdatePayload(form));

  const current = await getPublication(id);
  const existingGallery = (current && current.gallery) || [];
  const existingRelations = (current && current.publications_related) || [];

  if ("gallery_upload" in form || "gallery" in form) {
    const normalizedGallery = normalizeGalleryFromUpload({
      gallery_upload: form.gallery_upload || form.gallery || [],
    });

    if (existingGallery.length) {
      await Promise.all(
        existingGallery.map((item) => deletePublicationGalleryItem(id, item.id))
      );
    }

    if (normalizedGallery.length) {
      await Promise.all(
        normalizedGallery.map((item) => addPublicationGalleryItem(id, item))
      );
    }
  }

  if ("publications_related" in form) {
    const desiredRelations = normalizeRelatedPublications(form);
    const desiredIds = new Set(
      desiredRelations.map((item) => item.related_publication)
    );

    const byRelatedId = new Map();
    for (const rel of existingRelations) {
      const key = Number(rel.related_publication);
      if (Number.isNaN(key)) continue;
      if (!byRelatedId.has(key)) byRelatedId.set(key, []);
      byRelatedId.get(key).push(rel);
    }

    const toDelete = [];

    for (const [relatedId, list] of byRelatedId.entries()) {
      const inDesired = desiredIds.has(relatedId);
      if (!inDesired) {
        toDelete.push(...list);
      } else if (list.length > 1) {
        // оставляем первую связь, остальные удаляем
        toDelete.push(...list.slice(1));
      }
    }

    const toAdd = desiredRelations.filter(
      (item) => !byRelatedId.has(item.related_publication)
    );

    if (toDelete.length) {
      await Promise.all(
        toDelete.map((item) => deletePublicationRelated(id, item.id))
      );
    }

    if (toAdd.length) {
      await Promise.all(
        toAdd.map((item) =>
          addPublicationRelated(id, {
            related_publication: item.related_publication,
          })
        )
      );
    }
  }

  return getPublication(id).then(preparePublicationForm);
}

function fetchData(id) {
  return getPublication(id).then(preparePublicationForm);
}

export default {
  components: {
    Content,
    Form,
  },
  data() {
    return {
      scheme: defaultScheme,
      methods: {
        createData,
        updateData,
        fetchData,
        updateScheme,
      },
    };
  },
};
</script>
